import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Autocomplete from '@mui/material/Autocomplete';
import { createFilterOptions, TextField } from '@mui/material';

export interface ItemSelectorProps {
  disabled?: boolean;
  onChange: (value: string) => void;
  onBlur?: () => void;
  options: Array<{
    value: string;
    label: string;
  }>;
  valueSelected?: string;
  errorMessage?: string;
  name?: string;
  label?: string;
  fullWidth?: boolean;
}

export const ItemSelector = ({
  disabled,
  onChange,
  onBlur,
  options,
  valueSelected,
  errorMessage,
  name,
  label,
  fullWidth,
}: ItemSelectorProps): JSX.Element => {
  const { t } = useTranslation();

  const [selectedValue, setSelectedValue] = useState<string | null>(
    () =>
      options.find((option) => option.value === valueSelected)?.value ?? null
  );

  useEffect(() => {
    const newlySelectedOption =
      options.find((option) => option.value === valueSelected) ?? null;
    if (newlySelectedOption?.value !== selectedValue) {
      setSelectedValue(newlySelectedOption?.value ?? null);
    }
    // selectedValue missing to avoid circular dependency
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueSelected, options]);

  const selected = useMemo(() => {
    if (!selectedValue) {
      return null;
    }

    const selectedLabel = options.find(
      (option) => option.value === selectedValue
    )?.label;

    return {
      value: selectedValue,
      label: selectedLabel,
    };
  }, [options, selectedValue]);

  const defaultInputValue = selected?.label ?? '';

  const [inputValue, setInputValue] = useState(defaultInputValue);
  useEffect(() => {
    setInputValue(defaultInputValue);
  }, [defaultInputValue]);

  const value = useMemo(() => {
    return (
      selected ?? {
        value: '',
        label: '',
      }
    );
  }, [selected]);

  return (
    <Autocomplete
      fullWidth={fullWidth}
      data-cy="item-selector"
      disabled={disabled}
      openOnFocus={true}
      value={value}
      onChange={(_, newValue) => {
        onChange(newValue.value);
      }}
      onBlur={onBlur}
      filterOptions={createFilterOptions({ limit: 7 })}
      ListboxProps={{
        style: {
          maxHeight: '354px',
        },
      }}
      inputValue={inputValue}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      onInputChange={(_, value) => {
        setInputValue(value);
      }}
      disableClearable={true}
      renderInput={({ ...params }) => (
        <TextField
          name={name}
          label={label ?? t('common.select')}
          type={'text'}
          error={Boolean(errorMessage)}
          helperText={errorMessage}
          {...params}
        />
      )}
      options={options}
    />
  );
};
